import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, PauseCircle, PlayCircle, Gift } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listStoreBuyXGetYDeals,
  createStoreBuyXGetYDeal,
  updateStoreBuyXGetYDeal,
  deleteStoreBuyXGetYDeal,
} from "@/lib/api/admin";
import type { BuyXGetYDeal } from "@/lib/api/types";
import { ApiError } from "@/lib/api/fetch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { MultiBuyDealFormDialog } from "../dialogs/MultiBuyDealFormDialog";

function multiBuyDealErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code === "buy_x_get_y_deal_item_not_found") {
    return "The selected item could not be found.";
  }
  if (err instanceof ApiError && err.code === "buy_x_get_y_deal_invalid_discount") {
    return "The discount value is invalid for this item.";
  }
  return (err as Error).message;
}

type MultiBuyDealsTabProps = {
  storeId: string;
};

export function MultiBuyDealsTab({ storeId }: MultiBuyDealsTabProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingItem, setEditingItem] = useState<BuyXGetYDeal | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<BuyXGetYDeal | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    data: multiBuyDeals,
    isLoading,
    refetch: refetchMultiBuyDeals,
  } = useQuery({
    queryKey: ["multi-buy-deals", storeId],
    queryFn: () => listStoreBuyXGetYDeals(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["multi-buy-deals", storeId] });
  }

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof createStoreBuyXGetYDeal>[2]) =>
      createStoreBuyXGetYDeal(user!, storeId, data),
    onSuccess: () => {
      invalidate();
      setFormOpen(false);
      toast.success("Multi Buy deal created.");
    },
    onError: (err) => setApiError(multiBuyDealErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof updateStoreBuyXGetYDeal>[3];
    }) => updateStoreBuyXGetYDeal(user!, storeId, id, data),
    onSuccess: () => {
      invalidate();
      setFormOpen(false);
      setEditingItem(undefined);
      toast.success("Multi Buy deal updated.");
    },
    onError: (err) => setApiError(multiBuyDealErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteStoreBuyXGetYDeal(user!, storeId, id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast.success("Multi Buy deal deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (bd: BuyXGetYDeal) =>
      updateStoreBuyXGetYDeal(user!, storeId, bd.id, {
        name: bd.name,
        menu_item_id: bd.menu_item.id,
        buy_quantity: bd.buy_quantity,
        get_quantity: bd.get_quantity,
        discount_type: bd.discount_type,
        discount_value: bd.discount_value,
        max_claims_per_customer_per_day: bd.max_claims_per_customer_per_day,
        schedule: bd.schedule,
        status: bd.status === "active" ? "inactive" : "active",
      }),
    onSuccess: (_, bd) => {
      invalidate();
      toast.success(
        bd.status === "active" ? "Multi Buy deal paused." : "Multi Buy deal activated."
      );
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const formPending = createMutation.isPending || updateMutation.isPending;

  function handleOpenAdd() {
    setEditingItem(undefined);
    setApiError(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  }

  async function handleOpenEdit(bd: BuyXGetYDeal) {
    // Refresh first — the dialog seeds its state once on mount, from whatever it's given.
    const refreshed = await refetchMultiBuyDeals();
    const fresh = refreshed.data?.find((d) => d.id === bd.id) ?? bd;
    setEditingItem(fresh);
    setApiError(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  }

  function handleSave(data: {
    name: string;
    menu_item_id: string;
    buy_quantity: number;
    get_quantity: number;
    discount_type: "percent" | "amount";
    discount_value: number;
    max_claims_per_customer_per_day: number | null;
    schedule: Record<string, { open: string; close: string }[]>;
    status: "active" | "inactive";
  }) {
    setApiError(null);
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  }

  const items = multiBuyDeals ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <Button size="sm" onClick={handleOpenAdd}>
          <Plus className="size-4 mr-1" /> Add Multi Buy
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5 border-primary" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Gift}
          title="No Multi Buy deals"
          description='Create a buy-one-get-one deal like "Buy 2 Tacos, Get 1 at 50% off".'
        />
      ) : (
        <div className="space-y-2">
          {items.map((bd) => (
            <Card
              key={bd.id}
              onClick={() => handleOpenEdit(bd)}
              className="cursor-pointer hover:border-primary/50"
            >
              <CardContent className="flex items-center gap-3 p-3">
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">{bd.name}</p>
                    <StatusBadge status={bd.status} />
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-foreground truncate">
                      {bd.menu_item.name}
                    </span>
                    <span>· {bd.rule_label}</span>
                    {bd.max_claims_per_customer_per_day != null && (
                      <span>· max {bd.max_claims_per_customer_per_day}/day per customer</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    title={bd.status === "active" ? "Pause" : "Activate"}
                    disabled={toggleStatusMutation.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStatusMutation.mutate(bd);
                    }}
                  >
                    {bd.status === "active" ? (
                      <PauseCircle className="size-3.5" />
                    ) : (
                      <PlayCircle className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(bd);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Multi Buy deal"
        description={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />

      <MultiBuyDealFormDialog
        key={formKey}
        open={formOpen}
        onOpenChange={setFormOpen}
        storeId={storeId}
        item={editingItem}
        onSave={handleSave}
        isPending={formPending}
        apiError={apiError}
      />
    </div>
  );
}
